// DESCRIPTION: Lightweight rich-text editor for the event description field.
// DESCRIPTION: Renders a formatting toolbar (bold, italic, bullet list, link) that
// DESCRIPTION: wraps the current textarea selection in semantic HTML. The value is
// DESCRIPTION: always an HTML string, matching the REST API `content` field and the
// DESCRIPTION: [nemtly_event] shortcode output — no separate data model to drift from.
import { __ } from '@wordpress/i18n';
import { useRef } from '@wordpress/element';

/**
 * Wrap the current textarea selection in an opening/closing HTML tag pair.
 *
 * @since PBOOKING_SINCE
 *
 * @param {HTMLTextAreaElement} ta    The textarea element.
 * @param {string}              open  Opening HTML fragment.
 * @param {string}              close Closing HTML fragment.
 *
 * @return {{value:string,caretStart:number,caretEnd:number}|null} Result with the new value and the caret range to restore, or null when nothing is selected.
 */
const wrapSelection = ( ta, open, close ) => {
	const { selectionStart: start, selectionEnd: end, value } = ta;
	if ( start === end ) {
		return null;
	}
	const selected = value.slice( start, end );
	const next =
		value.slice( 0, start ) + open + selected + close + value.slice( end );
	return {
		value: next,
		caretStart: start + open.length,
		caretEnd: end + open.length,
	};
};

/**
 * Turn the current selection into a <ul> of <li> items (one per line).
 *
 * @since PBOOKING_SINCE
 *
 * @param {HTMLTextAreaElement} ta The textarea element.
 *
 * @return {{value:string,caretStart:number,caretEnd:number}|null} Result or null when nothing is selected.
 */
const listSelection = ( ta ) => {
	const { selectionStart: start, selectionEnd: end, value } = ta;
	if ( start === end ) {
		return null;
	}
	const selected = value.slice( start, end ).trim();
	if ( ! selected ) {
		return null;
	}
	const items = selected
		.split( /\r?\n/ )
		.map( ( line ) => `\t<li>${ line.trim() }</li>` )
		.join( '\n' );
	const block = `<ul>\n${ items }\n</ul>`;
	const next = value.slice( 0, start ) + block + value.slice( end );
	return {
		value: next,
		caretStart: start,
		caretEnd: start + block.length,
	};
};

/**
 * Prompt for a URL and link the current selection.
 *
 * @since PBOOKING_SINCE
 *
 * @param {HTMLTextAreaElement} ta The textarea element.
 *
 * @return {{value:string,caretStart:number,caretEnd:number}|null} Result or null if cancelled / nothing selected.
 */
const linkSelection = ( ta ) => {
	const url = window.prompt(
		__( 'Enter the link URL', 'nemtly-booking' ),
		'https://'
	);
	if ( ! url || url === 'https://' ) {
		return null;
	}
	return wrapSelection( ta, `<a href="${ url }">`, '</a>' );
};

/**
 * RichDescription — textarea with a small formatting toolbar.
 *
 * The toolbar buttons use onMouseDown + preventDefault so the textarea keeps
 * its selection when a button is pressed (the standard rich-text trick).
 *
 * @since PBOOKING_SINCE
 *
 * @param {Object}   props
 * @param {string}   props.value    Current HTML string.
 * @param {Function} props.onChange Called with the new HTML string.
 *
 * @return {JSX.Element}
 */
const RichDescription = ( { value, onChange } ) => {
	const ref = useRef( null );

	const apply = ( transform ) => {
		const ta = ref.current;
		if ( ! ta ) {
			return;
		}
		const result = transform( ta );
		if ( ! result ) {
			return;
		}
		onChange( result.value );
		// Restore the selection after React re-renders the new value.
		requestAnimationFrame( () => {
			ta.focus();
			ta.setSelectionRange( result.caretStart, result.caretEnd );
		} );
	};

	const toolbarBtn =
		'nb-inline-flex nb-items-center nb-justify-center nb-h-8 nb-min-w-8 nb-px-2 nb-text-sm nb-font-semibold nb-text-neutral-700 nb-bg-neutral-0 nb-border nb-border-neutral-300 nb-rounded-md nb-cursor-pointer nb-transition-colors hover:nb-bg-neutral-50 focus:nb-bg-accent-50';

	return (
		<div className="nemtly-description-editor">
			<div
				className="nb-flex nb-items-center nb-gap-1 nb-p-1 nb-bg-neutral-50 nb-border nb-border-neutral-300 nb-rounded-t-md"
				role="toolbar"
				aria-label={ __(
					'Description formatting',
					'nemtly-booking'
				) }
			>
				<button
					type="button"
					className={ toolbarBtn }
					aria-label={ __( 'Bold', 'nemtly-booking' ) }
					title={ __( 'Bold', 'nemtly-booking' ) }
					onMouseDown={ ( e ) => {
						e.preventDefault();
						apply( ( ta ) =>
							wrapSelection( ta, '<strong>', '</strong>' )
						);
					} }
				>
					<span className="nb-font-bold">B</span>
				</button>
				<button
					type="button"
					className={ toolbarBtn }
					aria-label={ __( 'Italic', 'nemtly-booking' ) }
					title={ __( 'Italic', 'nemtly-booking' ) }
					onMouseDown={ ( e ) => {
						e.preventDefault();
						apply( ( ta ) =>
							wrapSelection( ta, '<em>', '</em>' )
						);
					} }
				>
					<span className="nb-italic">I</span>
				</button>
				<button
					type="button"
					className={ toolbarBtn }
					aria-label={ __( 'Bullet list', 'nemtly-booking' ) }
					title={ __( 'Bullet list', 'nemtly-booking' ) }
					onMouseDown={ ( e ) => {
						e.preventDefault();
						apply( listSelection );
					} }
				>
					<span aria-hidden="true">•&ndash;</span>
				</button>
				<button
					type="button"
					className={ toolbarBtn }
					aria-label={ __( 'Link', 'nemtly-booking' ) }
					title={ __( 'Link', 'nemtly-booking' ) }
					onMouseDown={ ( e ) => {
						e.preventDefault();
						apply( linkSelection );
					} }
				>
					<span aria-hidden="true">🔗</span>
				</button>
			</div>

			<textarea
				ref={ ref }
				className="nb-w-full nb-min-h-[200px] nb-p-4 nb-border nb-border-t-0 nb-border-neutral-300 nb-rounded-b-md nb-text-sm nb-resize-y focus:nb-border-accent-500"
				value={ value }
				onChange={ ( e ) => onChange( e.target.value ) }
				placeholder={ __(
					'Add a description for your event…',
					'nemtly-booking'
				) }
			/>
			<p className="nb-text-xs nb-text-neutral-500 nb-mt-1 nb-m-0">
				{ __(
					'Select text and use the toolbar to format it. HTML is supported.',
					'nemtly-booking'
				) }
			</p>
		</div>
	);
};

export default RichDescription;
